import { isObject, isPlainObject, pulseMerge } from '@spt-tracking/utils';
import { generateObjectSDRN } from './utils';
/**
 * Format UIElement.
 *
 * This will transform the object to a valid UIElement.
 * When building the id of the UIElement it will primarily
 * try to use 'page.id' and 'page.type' (if available).
 * If page is not available it will fall back to use
 * 'target.id' and 'target.type' when calling sdrn().
 * If neither page nor target is available it will
 * use default type from the object.
 *
 * @private
 * @param data object and provider is mandatory, rest is optional.
 */
function formatUIElement({ object, provider, page, target }) {
    const { id, type, custom: _custom, 'spt:custom': _, ...rest } = object || {};
    const { id: pageId, type: pageType } = page || {};
    const { id: targetId, type: targetType } = target || {};
    const { id: providerId } = provider || {};
    const uiElementId = pageType || targetType
        ? `${generateObjectSDRN(pageId || targetId, providerId, pageType || targetType) || ''}:element:${id}`
        : generateObjectSDRN(id, providerId, type);
    const baseObject = {
        '@id': uiElementId,
        '@type': type,
    };
    return pulseMerge({}, baseObject, rest);
}
function formatDefaultObject({ object, provider }) {
    const { id, type = 'Content', custom: _custom, 'spt:custom': _, ...rest } = object || {};
    const { id: providerId } = provider || {};
    const objectId = generateObjectSDRN(id, providerId, type);
    let baseObject = {};
    if (objectId) {
        baseObject = {
            '@id': objectId,
            '@type': type,
        };
    }
    return pulseMerge({}, baseObject, rest);
}
function getObjectFormatter(type) {
    return type === 'UIElement' ? formatUIElement : formatDefaultObject;
}
/**
 * Format object.
 *
 * This function will recursively format itself and all nested object.
 * The initialPage parameter is optional and will be used in the recursive
 * nature to always pass in the root object page to each formatting.
 *
 * @private
 * @param input
 * @param pageInput Optional
 */
export const formatObject = (input, pageInput) => {
    const { object: objectInput = {} } = input || {};
    const { page: pageInObject } = objectInput;
    const page = pageInput || pageInObject;
    const evaluatedObjectInput = Object.keys(objectInput).reduce((accl, key) => {
        const prop = objectInput[key];
        if (Array.isArray(prop)) {
            return {
                // biome-ignore lint/performance/noAccumulatingSpread: -
                ...accl,
                [key]: prop.map((o) => {
                    if (isPlainObject(o)) {
                        return formatObject(Object.assign({}, input, { object: o }), page);
                    }
                    return o;
                }),
            };
        }
        if (isObject(prop)) {
            return {
                // biome-ignore lint/performance/noAccumulatingSpread: -
                ...accl,
                [key]: formatObject(Object.assign({}, input, { object: prop }), page),
            };
        }
        // biome-ignore lint/performance/noAccumulatingSpread: -
        return { ...accl, [key]: prop };
    }, {});
    const output = Object.assign({}, input, { page, object: evaluatedObjectInput });
    return getObjectFormatter(objectInput.type)(output);
};
export default formatObject;
export const defaultValue = () => undefined;
